import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input, Table } from 'reactstrap';
import axios from 'axios';

import { api } from '../../../config';
import logger from '../../../utils/logger';
import { SkeletonTable } from '../../../Components/Common/Skeleton';
import { formatNumber as formatNumberBase } from '../../../utils/format';
import { compareByCreatedDateAsc } from '../../../Components/QuantityTable';
import { cargoNeedsFreight } from '../../../shared/offer-domain/delivery/freight';

/* =========================================================
   DATA: /procurement/get-delivery-parcel (berpaginasi — kirim PageNumber/PageSize)
   ========================================================= */
export const useDeliveryParcels = (procurementId, token) => {
  const [response, setResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(procurementId));
  const [error, setError] = useState(null);

  const fetchParcels = useCallback(async () => {
    if (!procurementId || !token) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await axios.get(
        `${api.API_URL}/procurement/get-delivery-parcel?PageNumber=1&PageSize=100&procurementId=${procurementId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Interceptor axios global sudah meng-unwrap jadi response.data;
      // extractCargoList menelusuri input / input.data / input.data.data.
      setResponse(result);
    } catch (err) {
      // Interceptor menolak dengan string (bahkan undefined), bukan Error —
      // jangan pernah baca .message langsung di sini.
      const status = err?.response?.status;
      logger.error(
        'Error fetching delivery parcel:',
        status ? `HTTP ${status}` : (err?.message ?? String(err))
      );
      setError(err ?? new Error('Unknown error'));
      setResponse(null);
    } finally {
      setIsLoading(false);
    }
  }, [procurementId, token]);

  useEffect(() => {
    fetchParcels();
  }, [fetchParcels]);

  return { response, isLoading, error, refresh: fetchParcels };
};

/* =========================================================
   HELPER
   ========================================================= */
const formatNumber = value => formatNumberBase(value, null);

function naturalCompare(a = '', b = '') {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function isFob(line) {
  return (
    String(line?.incoterm || '')
      .trim()
      .toUpperCase() === 'FOB'
  );
}

function uniqueList(values) {
  return [...new Set(values.map(v => String(v ?? '').trim()).filter(Boolean))];
}

const isOriginRoute = route =>
  String(route ?? '')
    .trim()
    .toLowerCase() === 'origin';

// Satu entri = satu baris tabel.
// Lokasi ber-route "Origin" dipecah satu-satu; route lain digabung koma jadi satu entri.
function buildLoadingEntries(fobLines) {
  return fobLines.flatMap(line => {
    const locations = [
      ...(line.incotermFOBLocations || []),
      ...(line.incotermFOB2Locations || []),
    ].filter(loc => loc?.name);

    if (!locations.length) return [{ line, location: null, label: 'FOB' }];

    const routeOf = loc => loc.route || line.route || '';
    const format = loc => {
      const route = routeOf(loc);
      return route ? `${route} ${loc.name}` : loc.name;
    };

    const grouped = locations.filter(loc => !isOriginRoute(routeOf(loc)));
    const split = locations.filter(loc => isOriginRoute(routeOf(loc)));

    // route non-Origin (Straits, dll) → satu baris tergabung. `location` null
    // karena baris ini tidak mewakili satu lokasi tertentu, sehingga id dan
    // angka freight-nya diambil dari entri listData.
    const entries = grouped.length
      ? [
          {
            line,
            location: null,
            label: `FOB - ${grouped.map(format).join(', ')}`,
          },
        ]
      : [];

    return [
      ...entries,
      // route Origin → satu baris per lokasi; id dan angka freight-nya
      // diambil dari lokasi itu sendiri
      ...split.map(loc => ({
        line,
        location: loc,
        label: `FOB - ${format(loc)}`,
      })),
    ];
  });
}

/* ---------------------------------------------------------
   Normalisasi: menerima payload penuh, res.data dari axios,
   objek data, atau array mentah — semuanya jadi array cargo.
   --------------------------------------------------------- */
export function extractCargoList(input) {
  if (!input) return [];
  if (Array.isArray(input)) return input;
  if (Array.isArray(input.data)) return input.data; // { data: [...] }
  if (Array.isArray(input.data?.data)) return input.data.data; // payload penuh
  if (Array.isArray(input.data?.data?.data)) return input.data.data.data; // res axios utuh
  return [];
}

/* ---------------------------------------------------------
   Satu baris = satu entri loading (lihat buildLoadingEntries).
   Cargo ber-route "Origin" pecah jadi beberapa baris — satu per
   lokasi — sedangkan route lain tetap satu baris tergabung.
   - LOADING  : label entri tersebut.
   - Kolom lain diambil dari level cargo, jadi nilainya terulang
     di setiap baris pecahan.
   - DISCHARGED 1/2/3 : port1/port2/port3 dari SELURUH entri
     listData pada cargo tersebut (FOB maupun non-FOB)
     digabung, dibuang yang kosong dan duplikat, lalu diisi
     berurutan ke tiga kolom.
   --------------------------------------------------------- */
export function mapDeliveryToRows(response, { preserveOrder = false } = {}) {
  const cargoList = [...extractCargoList(response)];
  // preserveOrder: input sudah diurut di RequirementTab sesuai urutan Q&D
  // (createdDate asc via qtyDeliveryData) — jangan di-sort ulang.
  if (!preserveOrder) {
    cargoList.sort((a, b) => {
      const byDate = compareByCreatedDateAsc(a, b);
      if (byDate !== 0) return byDate;
      return naturalCompare(
        String(a?.deliveryUnit ?? ''),
        String(b?.deliveryUnit ?? '')
      );
    });
  }
  return (
    cargoList
      // hanya cargo FOB + CFR/DAP/CIF yang butuh freight; FOB saja tidak ditampilkan
      .filter(cargo => cargoNeedsFreight(cargo.listData || []))
      .flatMap((cargo, index) => {
        const lines = cargo.listData || [];
        const fobLines = lines.filter(isFob);
        const otherLines = lines.filter(line => !isFob(line));
        const ports = uniqueList(
          lines.flatMap(l => [l.port1, l.port2, l.port3])
        );
        const cargoId = cargo.id ?? `cargo-${index}`;

        return buildLoadingEntries(fobLines).map((entry, entryIndex) => ({
          id: `${cargoId}-${entryIndex}`, // key React harus unik antar baris pecahan
          source: {
            cargo,
            lines,
            fobLines,
            otherLines,
            ports,
            loading: entry.label,
            location: entry.location,
            fobLine: entry.line,
          },
        }));
      })
  );
}

/* =========================================================
   KOLOM + MAPPING KE JSON
   ========================================================= */
// Angka freight dibaca dari lokasi kalau barisnya route Origin, dan dari entri
// listData kalau barisnya route lain (Straits, dll).
const freightValueOf = key =>
  function readFreightValue({ fobLine, location }) {
    return formatNumber((location ?? fobLine)?.[key]);
  };

export const CARGO_DELIVERY_COLUMNS = [
  {
    key: 'id',
    header: 'ID',
    // hanya untuk export — tidak dirender di tabel (lihat VISIBLE_CARGO_DELIVERY_COLUMNS)
    hidden: true,
    // listData[].id dari entri FOB milik baris ini
    accessor: ({ fobLine }) => fobLine?.id,
  },
  {
    key: 'incotermLocationId',
    header: 'IncotermLocationId',
    // ikut disembunyikan seperti kolom ID — hanya muncul di export
    hidden: true,
    // dikosongkan (bukan '-') saat baris bukan route Origin
    emptyValue: '',
    // incotermFOBLocations[].id; route non-Origin seperti Straits dibiarkan kosong
    accessor: ({ location }) => location?.id ?? '',
  },
  // === LEBAR KOLOM DIATUR DI SINI ===
  // `width` di bawah dipakai pada <th> dan dipatuhi karena tabelnya
  // memakai table-layout: fixed. Jaga totalnya tetap 100%.
  {
    key: 'loading',
    header: 'LOADING',
    width: '20%',
    // incoterm FOB + (route + name) dari incotermFOBLocations / incotermFOB2Locations
    accessor: ({ loading }) => loading,
  },
  {
    key: 'cargo',
    header: 'CARGO',
    width: '6%',
    accessor: ({ cargo }) => cargo.deliveryUnit, // data.deliveryUnit
  },
  {
    key: 'volume',
    header: 'VOLUME',
    width: '5%',
    align: 'right',
    // deliveryBasis (nilai unik per cargo)
    accessor: ({ lines }) =>
      uniqueList(lines.map(line => formatNumber(line.deliveryBasis))).join(
        ', '
      ),
  },
  {
    key: 'aldAdd',
    header: 'ALD/ADD',
    width: '11%',
    accessor: ({ lines }) =>
      uniqueList(lines.map(line => line.addAld)).join(', '), // addAld
  },
  {
    key: 'd1',
    header: 'DISCHARGED 1',
    width: '7%',
    accessor: ({ ports }) => ports[0], // port1 []
  },
  {
    key: 'd2',
    header: 'DISCHARGED 2',
    width: '7%',
    accessor: ({ ports }) => ports[1], // port2 []
  },
  {
    key: 'd3',
    header: 'DISCHARGED 3',
    width: '7%',
    accessor: ({ ports }) => ports[2], // port3 []
  },
  // Empat kolom berikut menampilkan angka asli di layar dan juga diekspor
  // dengan nilai yang sama ke file Excel.
  {
    key: 'freight',
    header: 'FREIGHT LUMPSUM (USD)',
    width: '11%',
    accessor: freightValueOf('freightLumpsum'),
    align: 'right',
  },
  {
    key: 'laytime',
    header: 'LAYTIME (HOURS)',
    width: '8%',
    accessor: freightValueOf('laytime'),
    align: 'right',
  },
  {
    key: 'demurrage',
    header: 'DEMURRAGE PDPR (PER DAY PRO RATE)',
    width: '10%',
    accessor: freightValueOf('demuragePDPR'),
    align: 'right',
  },
  {
    key: 'r2',
    header: 'R2 (%)',
    width: '5%',
    accessor: freightValueOf('r2'),
    align: 'right',
  },
];

// Kolom yang benar-benar dirender; yang ber-flag `hidden` hanya ikut ke file Excel.
const VISIBLE_CARGO_DELIVERY_COLUMNS = CARGO_DELIVERY_COLUMNS.filter(
  col => !col.hidden
);

// Baris siap-export: memakai accessor yang sama dengan tabel di layar,
// kecuali kolom yang menyediakan `exportAccessor` sendiri.
export const buildCargoDeliveryExportRows = (response, opts = {}) =>
  mapDeliveryToRows(response, opts).map(row =>
    CARGO_DELIVERY_COLUMNS.reduce((acc, column) => {
      const accessor = column.exportAccessor ?? column.accessor;
      const value = accessor(row.source);
      acc[column.key] =
        value === null || value === undefined || value === ''
          ? (column.emptyValue ?? '-')
          : value;
      return acc;
    }, {})
  );

/* =========================================================
   KOMPONEN TABEL
   ========================================================= */
export function CargoDeliveryTable({
  response,
  loading = false,
  error = null,
  preserveOrder = false,
}) {
  const [query, setQuery] = useState('');
  const rows = useMemo(
    () => mapDeliveryToRows(response, { preserveOrder }),
    [response, preserveOrder]
  );

  const filteredRows = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return rows;
    return rows.filter(row =>
      CARGO_DELIVERY_COLUMNS.some(col =>
        String(col.accessor(row.source) ?? '')
          .toLowerCase()
          .includes(keyword)
      )
    );
  }, [rows, query]);

  return (
    <div className='w-100'>
      <div className='d-flex justify-content-end mb-3'>
        <Input
          type='search'
          bsSize='sm'
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder='Cari cargo, port, incoterm…'
          style={{ maxWidth: '16rem' }}
        />
      </div>

      <div className='table-responsive'>
        {/* table-layout: fixed — tanpa ini `width` di <th> hanya dianggap saran
            dan browser tetap memakai lebar hasil pengukuran konten */}
        <Table
          striped
          bordered
          hover
          className='align-middle mb-0'
          style={{ tableLayout: 'fixed', minWidth: '1100px' }}
        >
          <thead className='table-light'>
            <tr>
              {VISIBLE_CARGO_DELIVERY_COLUMNS.map(col => (
                <th
                  key={col.key}
                  scope='col'
                  className='fw-semibold small text-uppercase'
                  style={{
                    width: col.width,
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                    lineHeight: 1.2,
                    verticalAlign: 'middle',
                  }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <SkeletonTable
                inline
                rows={4}
                columns={VISIBLE_CARGO_DELIVERY_COLUMNS.length}
              />
            )}
            {!loading && error && (
              <tr>
                <td
                  colSpan={VISIBLE_CARGO_DELIVERY_COLUMNS.length}
                  className='text-center text-danger py-4 small'
                >
                  Gagal memuat data. Coba muat ulang halaman.
                </td>
              </tr>
            )}
            {!loading && !error && filteredRows.length === 0 && (
              <tr>
                <td
                  colSpan={VISIBLE_CARGO_DELIVERY_COLUMNS.length}
                  className='text-center text-muted py-4 small'
                >
                  {query
                    ? 'Tidak ada data yang cocok dengan pencarian.'
                    : 'Belum ada cargo dengan loading FOB.'}
                </td>
              </tr>
            )}
            {!loading &&
              !error &&
              filteredRows.map(row => (
                <tr key={row.id}>
                  {VISIBLE_CARGO_DELIVERY_COLUMNS.map(col => {
                    const value = col.accessor(row.source);
                    const isEmpty =
                      value === null || value === undefined || value === '';
                    return (
                      <td
                        key={col.key}
                        className={`small align-top${
                          col.align === 'right' ? ' text-end' : ''
                        }${col.key === 'cargo' ? ' fw-medium' : ''}`}
                        style={{ wordBreak: 'break-word' }}
                      >
                        {isEmpty ? '-' : value}
                      </td>
                    );
                  })}
                </tr>
              ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

export default CargoDeliveryTable;
